import { Component, Inject, OnInit, Optional, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { DrugValidation } from '../../models/validate-customer-drug';
import { CustomerDrugInfo } from '../../models/customer-info'
//import { MatDialogRef } from '@angular/material/dialog';
import { IMToolService } from '../../services/im-tool.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { HttpErrorResponse } from '@angular/common/http';
import * as constants from 'src/app/config/app-constants';

import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AlertWizardComponent } from '../alert-wizard/alert-wizard.component';
import * as XLSX from 'xlsx';


@Component({
  selector: 'app-import-wizard-validate-customer-drug-list',
  templateUrl: './import-wizard-validate-customer-drug-list.component.html',
  styleUrl: './import-wizard-validate-customer-drug-list.component.scss',
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  standalone: false
})
export class ImportWizardValidateCustomerDrugListComponent implements OnInit {

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  displayedColumns = ['expand', 'customerDrugName', 'cddbDrugName', 'inputNdc', 'convertedNdc','strength','uom', 'match', 'action'];
  columnsToDisplayWithExpand = [...this.displayedColumns];
  expandedElement: DrugValidation | null = null;

  drugList: DrugValidation[] = [];
  filteredDrugList: DrugValidation[] = [];
  selectedRow: any;
  customerId: string = '';
  dataSource = new MatTableDataSource<DrugValidation>();

  // Filter tracking
  activeFilter: 'all' | 'multiple' | 'single' | 'none' = 'all';
  filterCounts = {
    all: 0,
    multiple: 0,
    single: 0,
    none: 0
  };

  // Progress tracking
  progress = {
    totalRecords: 0,
    correctedRecords: 0,
    percentageComplete: 0
  };

  // Action options
  actionOptions = [
    { value: 'Accept', label: 'Accept' },
    { value: 'New Drug', label: 'New Drug' },
    { value: 'Non-Oral Solid', label: 'Non-Oral Solid' },
    { value: 'Exclude', label: 'Exclude' }
  ];

  // Action options for "No Matches" rows: New Drug, Non-Oral Solid, or Exclude are valid
  noMatchActionOptions = [
    { value: 'New Drug', label: 'New Drug' },
    { value: 'Non-Oral Solid', label: 'Non-Oral Solid' },
    { value: 'Exclude', label: 'Exclude' }
  ];

  getActionOptionsForRow(row: DrugValidation): { value: string; label: string }[] {
    return row?.status === 'None' ? this.noMatchActionOptions : this.actionOptions;
  }

  constructor(
    public dialogRef: MatDialogRef<ImportWizardValidateCustomerDrugListComponent>,
    public dialog: MatDialog,
    private imtoolService: IMToolService,
    private router: Router,
    private ngxLoader: NgxUiLoaderService,
    private activateRoute: ActivatedRoute,
    private toastService: ToastService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.drugList = data.inputDatalist;
    this.filteredDrugList = [...this.drugList];
    this.customerId = data.customerId;
    this.dataSource.data = this.filteredDrugList;
  }

  ngOnInit(): void {

    for (let row of this.drugList) {
      const ndcs = row.matchNdc1?.split(',');
      // Only auto-select the first NDC for non-multiple rows; multiple rows start with no selection
      if (ndcs && ndcs.length > 0 && row.status !== 'Multiple') {
        row.selectedMatchNdc = ndcs[0];
      } else if (row.status === 'Multiple') {
        row.selectedMatchNdc = undefined;
        row.action = undefined;
        row.isCorrected = false;
      }

      // Initialize action based on match status
      if (row.status === 'Exact') {
        row.action = 'Accept';
        row.isCorrected = true;
      } else if (row.status === 'None') {
        row.action = 'New Drug';
        row.isCorrected = true;
      }

      // Set match count
      if (ndcs) {
        row.matchCount = ndcs.length;
      }
    }

    this.calculateFilterCounts();
    this.updateProgress();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
  }

  /**
   * Toggle row expansion for multiple matches
   */
  toggleRow(element: DrugValidation): void {
    if (element.matchCount && element.matchCount > 1) {
      this.expandedElement = this.expandedElement === element ? null : element;
    }
  }

  /**
   * Handle action selection change
   */
  onActionChange(row: DrugValidation, action: string): void {
    row.action = action;
    row.isCorrected = action !== null && action !== '';
    this.updateProgress();
  }

  /**
   * Handle match selection for multiple matches
   */
  onMatchSelect(row: DrugValidation, selectedNdc: string): void {
    row.selectedMatchNdc = selectedNdc;
    const ndcs = row.matchNdc1?.split(',').map(x => x.trim()) || [];
    const idx = ndcs.indexOf(selectedNdc);
    const cddbNames = row.cddbDrugName?.split(',').map(x => x.trim()) || [];
    const strengths = row.strength?.split(',').map(x => x.trim()) || [];
    const uoms = row.uom?.split(',').map(x => x.trim()) || [];
    row.selectedCddbDrugName = idx >= 0 && cddbNames[idx] ? cddbNames[idx] : (cddbNames[0] || row.cddbDrugName);
    row.selectedStrength = idx >= 0 && strengths[idx] ? strengths[idx] : null;
    row.selectedUom = idx >= 0 && uoms[idx] ? uoms[idx] : null;
    row.action = 'Accept';
    row.isCorrected = true;
    this.updateProgress();
  }

  /**
   * Get match icon based on status
   */
  getMatchIcon(row: DrugValidation): string {
    if (row.status === 'None') return 'cancel';
    if (row.status === 'Exact') return 'check_circle';
    if (row.matchCount && row.matchCount > 1) return 'error';
    return 'check_circle';
  }

  /**
   * Get match color based on status
   */
  getMatchColor(row: DrugValidation): string {
    if (row.status === 'None') return 'text-red-600';
    if (row.status === 'Exact') return 'text-green-600';
    if (row.matchCount && row.matchCount > 1) return 'text-orange-600';
    return 'text-yellow-600';
  }

  /**
   * Use explicit icon colors to avoid dynamic class purge issues.
   */
  getMatchIconColor(row: DrugValidation): string {
    if (row.status === 'None') return '#dc2626';
    if (row.status === 'Exact') return '#16a34a';
    if (row.matchCount && row.matchCount > 1) return '#f59e0b';
    return '#f59e0b';
  }

  /**
   * Get match text
   */
  getMatchText(row: DrugValidation): string {
    if (row.status === 'None') return '0 CDDB Matches';
    if (row.status === 'Exact') return '1 CDDB Match';
    if (row.matchCount && row.matchCount > 1) return `${row.matchCount} CDDB Matches`;
    return 'Partial match';
  }

  /**
   * Calculate filter counts
   */
  private calculateFilterCounts(): void {
    this.filterCounts.all = this.drugList.length;
    this.filterCounts.multiple = this.drugList.filter(r => r.matchCount && r.matchCount > 1).length;
    this.filterCounts.single = this.drugList.filter(r => r.matchCount === 1 || r.status === 'Exact').length;
    this.filterCounts.none = this.drugList.filter(r => r.status === 'None' || r.matchCount === 0).length;
  }

  /**
   * Apply filter
   */
  applyFilter(filterType: 'all' | 'multiple' | 'single' | 'none'): void {
    this.activeFilter = filterType;

    switch (filterType) {
      case 'all':
        this.filteredDrugList = [...this.drugList];
        break;
      case 'multiple':
        this.filteredDrugList = this.drugList.filter(r => r.matchCount && r.matchCount > 1);
        break;
      case 'single':
        this.filteredDrugList = this.drugList.filter(r => r.matchCount === 1 || r.status === 'Exact');
        break;
      case 'none':
        this.filteredDrugList = this.drugList.filter(r => r.status === 'None' || r.matchCount === 0);
        break;
    }

    this.dataSource.data = this.filteredDrugList;
    if (this.paginator) {
      this.paginator.firstPage();
    }
  }

  getActiveFilterIndex(): number {
    switch (this.activeFilter) {
      case 'multiple':
        return 1;
      case 'single':
        return 2;
      case 'none':
        return 3;
      case 'all':
      default:
        return 0;
    }
  }

  onFilterTabChange(index: number): void {
    const filters: Array<'all' | 'multiple' | 'single' | 'none'> = ['all', 'multiple', 'single', 'none'];
    this.applyFilter(filters[index] || 'all');
  }

  /**
   * Update progress indicator
   */
  private updateProgress(): void {
    const records = this.drugList;
    this.progress.totalRecords = records.length;
    this.progress.correctedRecords = records.filter(r => r.isCorrected).length;

    if (this.progress.totalRecords === 0) {
      this.progress.percentageComplete = 0;
      return;
    }

    const rawPercentage = (this.progress.correctedRecords / this.progress.totalRecords) * 100;
    const roundedPercentage = Math.round(rawPercentage * 100) / 100;

    this.progress.percentageComplete = this.progress.correctedRecords < this.progress.totalRecords
      ? Math.min(roundedPercentage, 99.99)
      : 100;
  }

  /**
   * Check if all records are corrected
   */
  isAllCorrected(): boolean {
    return this.progress.correctedRecords === this.progress.totalRecords && this.progress.totalRecords > 0;
  }


  closeDialog(response: boolean) {
    this.dialogRef.close({ event: 'close', data: response })
  }

  /**
   * Complete and import accepted records
   */
  onComplete(): void {
    const categorizedRecords = this.categorizeRecords();
    const totalRecords = categorizedRecords.accepted.length +
      categorizedRecords.excluded.length +
      categorizedRecords.newDrug.length +
      categorizedRecords.nonOralSolid.length;

    if (totalRecords === 0) {
      this.toastService.openToast(
        'No records to import. Please select actions for all records.',
        constants.ToastPanelType.warning,
        2
      );
      return;
    }

    this.openCompleteWindow(categorizedRecords, totalRecords);
  }

  /**
   * Categorize records based on action
   * For multiple matches, only include the selected NDC
   */
  private categorizeRecords() {
    const prepareRecord = (record: DrugValidation) => {
      // Determine boolean flags based on action
      const invalid_ndc = record.action === 'Exclude';
      const new_drug = record.action === 'New Drug';
      const non_oral_solid = record.action === 'Non-Oral Solid';

      // For multiple matches, use the selected NDC
      if (record.matchCount && record.matchCount > 1 && record.selectedMatchNdc) {
        return {
          ...record,
          matchNdc1: record.selectedMatchNdc,
          matchNdc2: null,
          matchNdc3: null,
          invalid_ndc,
          new_drug,
          non_oral_solid,
          usage: record.usage || 0
        };
      }
      return {
        ...record,
        invalid_ndc,
        new_drug,
        non_oral_solid,
        usage: record.usage || 0
      };
    };

    return {
      accepted: this.drugList
        .filter(r => r.action === 'Accept' && r.isCorrected)
        .map(prepareRecord),
      excluded: this.drugList
        .filter(r => r.action === 'Exclude' && r.isCorrected)
        .map(prepareRecord),
      newDrug: this.drugList
        .filter(r => r.action === 'New Drug' && r.isCorrected)
        .map(prepareRecord),
      nonOralSolid: this.drugList
        .filter(r => r.action === 'Non-Oral Solid' && r.isCorrected)
        .map(prepareRecord)
    };
  }

  openCompleteWindow(categorizedRecords: any, totalRecords: number) {
    const { accepted, excluded, newDrug, nonOralSolid } = categorizedRecords;

    const dialogRef = this.dialog.open(AlertWizardComponent, {
      backdropClass: ['smDialog'],
      disableClose: true,
      data: {
        alertIcon: "/assets/img/icons/ico.16.alert.svg",
        title: "Complete Import?",
        bodyMessage: `Are you sure you want to import ${totalRecords} record(s)?`,
        bodyMessage2: `Accepted: ${accepted.length}, Excluded: ${excluded.length}, New Drug: ${newDrug.length}, Non-Oral Solid: ${nonOralSolid.length}`,
        bodyMessage2List: [
          { label: 'Accepted', value: accepted.length },
          { label: 'Excluded', value: excluded.length },
          { label: 'New Drug', value: newDrug.length },
          { label: 'Non-Oral Solid', value: nonOralSolid.length },
        ],
        cancelBtnText: "Cancel",
        confirmBtnText: "Complete",
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result.data) {
        this.importAcceptedRecords(categorizedRecords);
      }
    });
  }

  importAcceptedRecords(categorizedRecords: any) {
    const actionMap: { [key: string]: string } = {
      Accept: 'Accepted',
      Exclude: 'Excluded',
      'New Drug': 'New Drug',
      'Non-Oral Solid': 'Non-Oral Solid',
    };

    const updateValidateDrug = this.drugList
      .filter((record) => record.isCorrected && !!record.action)
      .map((record) => {
        const convertedNdc = record.action === 'Accept'
          ? (record.selectedMatchNdc || (record.matchNdc1 ? record.matchNdc1.split(',')[0].trim() : null))
          : record.customerNdc;

        return {
          Ndc: record.customerNdc,
          ConvertedNdc: convertedNdc,
          Action: actionMap[record.action as string] || record.action,
        };
      })
      .filter((record) => !!record.ConvertedNdc);

    const payload = {
      customerId: this.customerId,
      updateValidateDrug,
    };

    const IMPORT_KEY = 'importAcceptedRecordsKey';

    this.ngxLoader.start(IMPORT_KEY);
    this.imtoolService.processValidatedDrugs(this.customerId, payload).subscribe(
      (result) => {
        this.ngxLoader.stop(IMPORT_KEY);
        if (result === true || result?.statusCode === 200 || result?.succeed === true) {
          // Directly navigate to WI2 without showing toast
          const closeData = { event: 'complete', data: true, navigateToWI2: true };
          this.dialogRef.close(closeData);
        } else {
          this.toastService.openToast(
            result.message,
            constants.ToastPanelType.error,
            2
          );
        }
      },
      (error: HttpErrorResponse) => {
        this.ngxLoader.stop(IMPORT_KEY);
        this.toastService.openToast(
          error.error.message ?? error.message,
          constants.ToastPanelType.error,
          2
        );
      }
    );
  }

  onAcceptAll() {
    this.openAcceptAllWindow();
  }

  openAcceptAllWindow() {
    const dialogRef = this.dialog.open(AlertWizardComponent, {
      backdropClass: ['smDialog'],
      disableClose: true,
      data: {
        alertIcon: "/assets/img/icons/ico.16.alert.svg",
        title: "Accept All NDC?",
        bodyMessage: "Are you sure you want to Accept all  NDC?",
        bodyMessage2: "This process cannot be undone.",
        cancelBtnText: "Cancel",
        confirmBtnText: "Accept All",
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result.data) {
        this.acceptAllCustomerNdc();
      }
    });
  }

  onAccept(customerNdc: any, selectedNdc: any) {
    this.openAcceptWindow(customerNdc, selectedNdc);
  }

  openAcceptWindow(customerNdc: any, selectedNdc: any) {
    const dialogRef = this.dialog.open(AlertWizardComponent, {
      backdropClass: ['smDialog'],
      disableClose: true,
      data: {
        alertIcon: "/assets/img/icons/ico.16.alert.svg",
        title: "Accept NDC?",
        bodyMessage: "Are you sure you want to Accept this NDC?",
        bodyMessage2: "This process cannot be undone.",
        cancelBtnText: "Cancel",
        confirmBtnText: "Accept",
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result.data) {
        this.acceptCustomerNdc(customerNdc, selectedNdc);
      }
    });
  }


  onDelete(ndc: any) {
    this.openAlertWindow(ndc);
  }

  openAlertWindow(ndc: any) {
    const dialogRef = this.dialog.open(AlertWizardComponent, {
      backdropClass: ['smDialog'],
      disableClose: true,
      data: {
        alertIcon: "/assets/img/icons/ico.16.alert.svg",
        title: "DELETE NDC?",
        bodyMessage: "Are you sure you want to delete this NDC?",
        bodyMessage2: "This process cannot be undone.",
        cancelBtnText: "Cancel",
        confirmBtnText: "Delete",
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result.data) {
        this.removeCustomerNdc(ndc);
      }
    });
  }

  removeDrugByNdc(ndc: string) {
    const removeData = this.drugList.filter(item => item.customerNdc === ndc);
    this.drugList = this.drugList.filter(item => !removeData.includes(item));
    this.calculateFilterCounts();
    this.applyFilter(this.activeFilter);
  }

  acceptCustomerNdc(customerNdc: any, convertedNdc: any) {
    const ACCEPTNDC = 'accepCustomerNdckey'
    this.ngxLoader.start(ACCEPTNDC);
    this.imtoolService.acceptCustomerNdc(this.customerId, customerNdc, convertedNdc).subscribe(
      (result) => {
        this.ngxLoader.stop(ACCEPTNDC);
        if (result.statusCode == 200) {
          this.toastService.openToast(
            result.message,
            constants.ToastPanelType.done,
            2
          );
          this.removeDrugByNdc(customerNdc);
        } else {
          this.toastService.openToast(
            result.message,
            constants.ToastPanelType.error,
            2
          );
        }
      },
      (error: HttpErrorResponse) => {
        this.ngxLoader.stop(ACCEPTNDC);
        this.toastService.openToast(
          error.error.message ?? error.message,
          constants.ToastPanelType.error,
          2
        );
      }
    );
  }

  acceptAllCustomerNdc() {
    const ACCEPTNDC = 'accepAllCustomerNdckey'
    this.ngxLoader.start(ACCEPTNDC);
    this.imtoolService.acceptAllCustomerNdc(this.customerId, this.drugList).subscribe(
      (result) => {
        this.ngxLoader.stop(ACCEPTNDC);
        if (result.statusCode === 200) {
          this.toastService.openToast(
            result.message,
            constants.ToastPanelType.done,
            2
          );
          this.dataSource.data = [];
        } else {
          this.toastService.openToast(
            result.message,
            constants.ToastPanelType.error,
            2
          );
        }
      },
      (error: HttpErrorResponse) => {
        this.ngxLoader.stop(ACCEPTNDC);
        this.toastService.openToast(
          error.error.message ?? error.message,
          constants.ToastPanelType.error,
          2
        );
      }
    );
  }

  removeCustomerNdc(customerNdc: string) {
    const REMOVENDC = 'removeCustomerNdckey';
    this.ngxLoader.start(REMOVENDC);
    this.imtoolService.removeCustomerNdc(this.customerId, customerNdc).subscribe(
      (result) => {
        this.ngxLoader.stop(REMOVENDC);
        if (result.statusCode === 200) {
          this.toastService.openToast(
            result.message,
            constants.ToastPanelType.done,
            2
          );
          this.removeDrugByNdc(customerNdc);
        } else {
          this.toastService.openToast(
            result.message,
            constants.ToastPanelType.error,
            2
          );
        }
      },
      (error: HttpErrorResponse) => {
        this.ngxLoader.stop(REMOVENDC);
        console.log(error);
        this.toastService.openToast(
          error.error.message ?? error.message,
          constants.ToastPanelType.error,
          2
        );
      }
    );
  }

  exportTable(): void {
    const table = document.getElementById('exportTable');
    if (!table) return;
    const clonedTable = table.cloneNode(true) as HTMLElement;

    clonedTable.querySelectorAll('mat-icon').forEach(icon => icon.remove());
    clonedTable.querySelectorAll('.no-export').forEach(el => el.remove());

    const worksheet = XLSX.utils.table_to_sheet(clonedTable);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, 'validate_drug_list');
    XLSX.writeFile(workbook, `Validate customer drugList-${this.getCurrentDate()}.xlsx`);
  }

  getCurrentDate(): string {
    const currentDate = new Date();
    const options: Intl.DateTimeFormatOptions = {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
    };
    return currentDate.toLocaleDateString('en-US', options);
  }

}
